import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { useTheme } from "@mui/styles";
import { Grid, Theme, Typography, Popover, alpha } from "@mui/material";
import AmplifyAPIWrapper from "src/API/AmplifyAPIWrapper";
import { TextField } from "src/components/FormikComponents/TextField";
import _ from "lodash";
import { ShortCodeChip, IShortCodeChip } from "./ShortCodeChip";
import classNames from "classnames";
import { IAirport, IFlight, IPairing, IUser, ISimpleUser } from "@vmosolutions/oc-interfaces";
import { findAirports } from "src/utils/airportHelper";
import { AirportChip, IAirportChip } from "./AirportChip";
import { LoadingMessage } from "../LoadingMessage/LoadingMessage";
import { useDispatch, useSelector } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { IAircraftFlights } from "src/redux/reducers/FlightTimelineReducer";
import { FlightChip } from "../Flights/FlightChip";
import { setSelectedEvents } from "src/redux/actions/flights/FlightTimeline.Actions";
import {
  getBasicPairingInfo,
  getCrewPositionInfo,
  getPairingLogicModifiers,
} from "src/pages/CrewSchedule/HoveredCrewSchedEvents";

import CrewScheduleEventSummaryList, {
  ISummaryItem,
  ISummarySubHeader,
} from "src/components/CrewSchedule/CrewScheduleEventSummaryList";
import { setSearchedCrewmembers } from "src/redux/actions/CrewSchedule/CrewSchedule.Actions";
import { PositionBadge } from "../CrewSchedule/PositionBadge";
import { openWindow } from "src/redux/actions/Mosaic.Actions";
import { v1 } from "uuid";
import { openDrawer } from "src/redux/actions/App.Actions";
import Portal from "@mui/material/Portal";
import { IconButton } from "src/components/IconButton/IconButton";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

interface IOption {
  label: string;
  value: string;
  name?: string;
  data?: IAirport | IFlight | IPairing | ISimpleUser;
  type?: "pairing" | "flight" | "airport" | "crew";
}

interface IGroupedOption {
  label: string;
  options: IOption[];
}

type ISelectFlight = (flight: IFlight) => void;
interface IShortCodeSearchProps {
  className?: string;
  hideInlineEdit?: boolean;
}

const ShortCodeSearchStyles = (theme: Theme): any => ({
  display: "flex",
  width: "auto",
  marginLeft: "10px",

  "& .searchshortcode_textfield": {
    width: 200,
    "& label": { display: "none" },
    "& .input_control div.input-base": {
      backgroundColor: "transparent",
      borderColor: theme.palette.primary.main,
      color: theme.palette.common.white,
      width: 200,
      transition: theme.transitions.create(["border"], {
        easing: theme.transitions.easing.easeIn,
        duration: theme.transitions.duration.standard,
      }),
      "& input": {
        color: theme.palette.common.white,
        "&::placeholder, &::-webkit-input-placeholder": {
          color: alpha(theme.palette.common.white, 0.75),
        },
      },
    },
    "&:hover, &.focused, &.menuopen": {
      "& .input_control div.input-base": {
        borderColor: alpha(theme.palette.primary.contrastText, 0.5),
      },
    },
    "&.focused": {
      "& .input_control div.input-base": {
        "&:after": {
          transform: "scaleX(1)",
          borderBottom: "1px solid " + alpha(theme.palette.primary.contrastText, 0.5),
        },
      },
    },
  },
  "& .loading_ellipsis": {
    marginTop: "4px",
    marginLeft: "-25px",
  },
  /** positions toggle button on the right of the search input box */
  "& .toggle_menu": {
    width: 20,
    height: 20,
    padding: "8px",
    marginTop: "8px",
    marginLeft: "-28px",
    borderRadius: "3px",
  },
});

const SearchMenuStyles = (theme: Theme): any => ({
  position: "absolute",
  zIndex: 9998, // The only menus which would be higher are the context menus for flights, pairings etc set at 9999 - CC
  top: 32,
  left: 145,
  backgroundColor: "transparent",
  boxShadow: "none",
  borderRadius: 0,
  "& .menu": {
    minHeight: 24,
    minWidth: 200,
    padding: "4px",
    border: `1px solid ${theme.custom.forms.inputBorder}`,
    backgroundColor: alpha(theme.custom.contextPopover.backgroundColor, 0.9),
    color: theme.custom.contextPopover.foreGroundColor,
    fontSize: 12,
    flexWrap: "nowrap",
    maxHeight: 500,
    overflowY: "auto",
    "& .close_menu": {
      backgroundColor: theme.custom.contextPopover.backgroundColor,
      position: "absolute",
      width: 20,
      height: 20,
      right: 5,
      borderRadius: "3px",
    },
    /* styled to match group headers in Select Field */
    "& .group_header": {
      color: "#999",
      cursor: "default",
      display: "block",
      fontSize: "75%",
      fontWeight: 500,
      padding: "12px 12px 8px 12px",
      textTransform: "uppercase",
      boxSizing: "border-box",
    },
    "& .option": {
      padding: "4px",
      minHeight: 32,
      borderRadius: "2px",
      "&:hover": {
        backgroundColor: theme.palette.background.paper,
        "& svg": { color: theme.custom.forms.inputText },
      },
    },
  },
});

const PairingPopoverStyles = (theme: Theme): any => ({
  backgroundColor: "transparent",
  boxShadow: "none",
  zIndex: 2000,
  "& .menu": {
    backgroundColor: alpha(theme.custom.contextPopover.backgroundColor, 0.9),
    color: theme.custom.contextPopover.foreGroundColor,
    boxShadow: theme.shadows[5],
    borderRadius: "5px",
    padding: "15px 0px",
    fontSize: 18,
    "& .pairing_id": { margin: "0px 15px", fontSize: 12 },
  },
});

// Async search for short codes.
const searchShortCode = async (inputValue: string): Promise<IOption[]> => {
  const response: IOption[] = await AmplifyAPIWrapper.Get(`/pairings/shortCode/${inputValue}`);
  if (Array.isArray(response) && response.length > 0) {
    const newOptions: IOption[] = response.map((code: any) => ({
      label: code.label,
      value: code.value,
      name: null,
      data: null,
      type: "pairing",
    }));
    return newOptions;
  } else {
    return [];
  }
};

// Async search for airport objects.
const searchAirports = async (inputValue: string): Promise<IOption[]> => {
  const response: IAirport[] = await findAirports(inputValue);
  if (Array.isArray(response) && response.length > 0) {
    const newOptions: IOption[] = response.map((ap) => ({
      label: ap.Ident,
      value: ap._id,
      name: ap.Name,
      data: ap,
      type: "airport",
    }));
    return newOptions;
  } else {
    return [];
  }
};

const searchUser = async (inputValue: string): Promise<IOption[]> => {
  const users: ISimpleUser[] = await AmplifyAPIWrapper.Get(`/users/search/${inputValue}`);
  if (Array.isArray(users)) {
    return users.reduce((acc: IOption[], user) => {
      if (user?.crewInfo?.isActive) {
        acc.push({
          label: `${user.firstName} ${user.lastName} ${user.employeeId ? `[${user.employeeId}]` : ""}`,
          value: user._id,
          name: user._id,
          data: user,
          type: "crew",
        });
      }
      return acc;
    }, []);
  }
  return [];
};

// Search airports vision within current timeline interval on all aircraft.
const searchFlights = (value: string, flightsInViewScale: IFlight[]): IOption[] => {
  // Search only if string can be evaluated as number, Return exact matches only.
  if (/^\d+$/.test(value)) {
    const flightNumber = parseInt(value);
    const results = flightsInViewScale.filter((flight) => flight.flightNumber === flightNumber);
    if (Array.isArray(results) && results.length > 0) {
      const newOptions: IOption[] = results.map((fl) => ({
        label: fl.flightNumber.toString(),
        value: fl._id,
        data: fl,
        type: "flight",
      }));
      return newOptions;
    } else {
      return [];
    }
  }
  return [];
};

/**
 * Format's the option into a component that can be rendered in the select menu.
 * @param option - contains the data for the given item.
 * @param hideInlineEdit - determines whether or not the action button appears.
 */
const formatSelectOptions = (
  option: IOption,
  hideInlineEdit: boolean,
  selectFlight: ISelectFlight,
  selectCrew: (crew: ISimpleUser) => void,
  setHoveredOption: (option: IOption) => void
): JSX.Element => {
  const { label, value, type, data, name } = option;

  if (type === "pairing") {
    const shortCode: IShortCodeChip = {
      itemId: value,
      code: label,
      type: type as "pairing",
      edit: !hideInlineEdit,
    };
    return (
      <Grid
        container
        alignItems="center"
        className={"option pairing"}
        onMouseEnter={() => setHoveredOption(option)}
        onMouseLeave={() => setHoveredOption(null)}
      >
        <ShortCodeChip {...shortCode} />
        <Typography style={{ marginLeft: 10 }} variant="body2" color="textPrimary">
          Pairing
        </Typography>
      </Grid>
    );
  } else if (type === "airport") {
    const airportChip: IAirportChip = {
      itemId: value,
      code: label,
      type: type as "airport",
      airport: data as IAirport,
      edit: !hideInlineEdit,
    };
    return (
      <Grid
        container
        alignItems="center"
        className={"option airport"}
        onMouseEnter={() => setHoveredOption(option)}
        onMouseLeave={() => setHoveredOption(null)}
      >
        <AirportChip {...airportChip} />
        <Typography style={{ marginLeft: 10 }} variant="body2" color="textPrimary">
          <span
            style={{
              display: "block",
              fontSize: 12,
              width: 90,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {name}
          </span>
        </Typography>
      </Grid>
    );
  } else if (type === "flight") {
    return (
      <Grid
        container
        alignItems="center"
        className={"option crew"}
        onMouseEnter={() => setHoveredOption(option)}
        onMouseLeave={() => setHoveredOption(null)}
      >
        <FlightChip data={data as IFlight} disableFocus={true} onClickChip={() => selectFlight(data as IFlight)} />
        <Typography style={{ marginLeft: 5, fontSize: 10 }} variant="body2" color="textPrimary">
          {(data as IFlight)?.aircraft?.registration ?? ""}
        </Typography>
      </Grid>
    );
  } else if (type === "crew") {
    const { firstName, lastName, employeeId, crewInfo } = data as IUser;
    const highPos = crewInfo?.positions?.sort((a, b) => a.displayOrder - b.displayOrder)[0];
    return (
      <Grid
        container
        alignItems="center"
        wrap="nowrap"
        className={"option crew"}
        onMouseEnter={() => setHoveredOption(option)}
        onMouseLeave={() => setHoveredOption(null)}
      >
        {highPos && <PositionBadge position={highPos} filled={true} isInactive={!crewInfo?.isActive} />}
        <Grid item style={{ marginLeft: 10 }} onClick={() => selectCrew(data as IUser)}>{`${lastName}, ${firstName} ${
          employeeId ? `(${employeeId})` : ""
        }`}</Grid>
      </Grid>
    );
  } else {
    return null;
  }
};

/**
 * ShortCodeSearch - Component used to search short codes and navigate to their edit pages.
 * @param props - Component Properties [[IShortCodeChip]]
 */
export const ShortCodeSearch = (props: IShortCodeSearchProps): JSX.Element => {
  const { className, hideInlineEdit } = props;
  const theme: Theme = useTheme();
  const [options, setOptions] = useState<IGroupedOption[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const dispatch = useDispatch();

  const searchRef = useRef<HTMLDivElement>();
  const [showResults, setShowResults] = useState<boolean>(false);

  const [hoveredOption, setHoveredOption] = useState<IOption>(null);
  const [pairingInfo, setPairingInfo] = useState<IPairing>(null);
  const displayPairingSummary = useMemo(() => {
    return hoveredOption?.type === "pairing" && hoveredOption?.value === pairingInfo?._id;
  }, [hoveredOption, pairingInfo]);

  // If user hides the result menu, clear hovered item.
  useEffect(() => {
    if (!showResults) setHoveredOption(null);
  }, [showResults]);

  // Addition used to display flights visible on timeline.
  const flightData: IAircraftFlights = useSelector((state: IRootState) => state.timelineFlights.flightData);
  const userSetScale = useSelector((state: IRootState) => state.timelineFlights.userSetScale);

  const flights: IFlight[] = useMemo(() => {
    let loadedFlights: IFlight[] = [];
    if (flightData) {
      for (const [, value] of Object.entries(flightData)) {
        loadedFlights = [...loadedFlights, ...value.flights];
      }
    }
    return loadedFlights;
  }, [flightData]);

  // Track flights that are visible within the current viewport (even offscreen vertically on other aircraft)
  const flightsInViewScale = useMemo(() => {
    if (!userSetScale) {
      return flights;
    }
    const [xmin, xmax] = userSetScale; // current visible area on timeoline. (epochs)
    return flights.filter(
      (flight: IFlight) =>
        (flight.scheduledOut.valueOf() > xmin && flight.scheduledOut.valueOf() < xmax) ||
        (flight.scheduledIn.valueOf() > xmin && flight.scheduledIn.valueOf() < xmax)
    );
    // console.log(leftTimeline, rightTimeline);
  }, [flights, userSetScale]);

  const selectFlight = useCallback((flight: IFlight) => dispatch(setSelectedEvents([flight])), [dispatch]);
  const selectCrew = useCallback(
    (user: ISimpleUser) => {
      const id = v1();
      dispatch(openDrawer());
      dispatch(openWindow("Crew Schedule", "CrewScheduleWindow", [], { id: id, path: [] }, null, true));
      dispatch(setSearchedCrewmembers(id, [user._id]));
    },
    [dispatch]
  );
  // Async Call to get results from both shortCodes and airports and combine them into one options array.
  // Debounce of 1 second applied so that people can't spam the fetch call.
  const fetchResults = useCallback(
    (value: string): void => {
      const getOptions = async (): Promise<void> => {
        setIsLoading(true);
        const [shortCodeOptions, airportOptions, crewOptions] = await Promise.all([
          searchShortCode(value),
          searchAirports(value),
          searchUser(value),
        ]);
        const flightOptions = searchFlights(value, flightsInViewScale);
        const groupedOptions = [
          { label: "Pairings", options: shortCodeOptions },
          { label: "Airports", options: airportOptions },
          { label: "Flights", options: flightOptions },
          { label: "Crew", options: crewOptions },
        ];
        setShowResults(true);
        setOptions(groupedOptions);
        setIsLoading(false);
      };
      getOptions();
    },
    [flightsInViewScale]
  );

  const renderSearchResults = useCallback(
    (groupedResults: IGroupedOption[]): JSX.Element => {
      const resultGroups = Object.values(groupedResults);
      return (
        <Grid container direction="column" className="menu">
          {resultGroups.map((group, index) => {
            return group.options.length > 0 ? (
              <Grid key={`search_group_container_${index}`} container direction="column">
                <Typography className="group_header">{group.label}</Typography>
                {group.options.map((result, resultIndex) => (
                  <div key={`search_group_${resultIndex}`}>
                    {formatSelectOptions(result, hideInlineEdit, selectFlight, selectCrew, setHoveredOption)}
                  </div>
                ))}
              </Grid>
            ) : null;
          })}
        </Grid>
      );
    },
    [hideInlineEdit, selectCrew, selectFlight]
  );

  // Wrapped in a debounce to ensure that a new search isn't run with every character press.
  const handleSearchTextChange = _.debounce((newVal: string): void => {
    // Don't return any results if search string is anything less than 4 characters
    if (newVal?.length === 0) {
      setShowResults(false);
      setOptions([]);
      return;
    }
    const value = newVal?.toLowerCase();
    fetchResults(value);
    return;
  }, 1000); // Increase debounced value on this call as it was hitting the backend more often than I wanted. - CC

  useEffect(() => {
    if (hoveredOption?.type === "pairing" && hoveredOption?.value) {
      if (!hoveredOption.data) {
        // GET PAIRING INFO
        const getPairingAction = async (): Promise<void> => {
          const loadedPairing = await AmplifyAPIWrapper.Get<IPairing>(`/pairings/${hoveredOption?.value}`, null);
          if (loadedPairing?._id) {
            setPairingInfo(loadedPairing);
          }
        };
        getPairingAction();
      }
    }
  }, [hoveredOption]);

  // Pairing Summary info if selected option if of type pairing.
  const pairingSummaryItems: (ISummaryItem | ISummarySubHeader | null)[] = useMemo(() => {
    if (pairingInfo?._id) {
      return [
        ...getBasicPairingInfo(pairingInfo, "Etc/UTC"),
        ...getCrewPositionInfo(pairingInfo?.positions),
        ...getPairingLogicModifiers(pairingInfo?.logicModifiers),
      ];
    }
    return [];
  }, [pairingInfo]);

  return (
    <Grid container ref={searchRef} sx={ShortCodeSearchStyles(theme)} className={classNames(!!className && className)}>
      <TextField
        id="header_shortcode_search"
        className={`searchshortcode_textfield ${showResults && "menuopen"}`}
        label=""
        fullWidth={false}
        onKeyDown={(event: any) => {
          if (event.key === "Enter") {
            event.stopPropagation();
            const searchString = event?.target?.value;
            if (searchString?.length > 0) {
              handleSearchTextChange(searchString);
            } else {
              setOptions([]);
            }
          }
        }}
        InputLabelProps={{ shrink: true }} // needed so that placeholder always appears.
        placeholder="Search..."
      />

      {isLoading ? (
        <div className="loading_ellipsis">
          <LoadingMessage message="" />
        </div>
      ) : (
        <IconButton className="toggle_menu" onClick={() => setShowResults(!showResults)} size="large">
          {!showResults ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
        </IconButton>
      )}

      {searchRef?.current &&
        showResults && ( // container={searchRef?.current}
          <Portal>
            <Grid item sx={SearchMenuStyles}>
              {renderSearchResults(options)}
            </Grid>
          </Portal>
        )}

      <Popover
        sx={PairingPopoverStyles(theme)}
        anchorEl={searchRef?.current}
        anchorReference="anchorPosition"
        anchorPosition={{ top: 0, left: 360 }}
        open={pairingSummaryItems?.length > 0 && displayPairingSummary && showResults}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        disableScrollLock={true}
      >
        <div className="menu">
          <CrewScheduleEventSummaryList hideTitle={true} items={pairingSummaryItems} />
        </div>
      </Popover>
    </Grid>
  );
};
